import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, ArrowLeft } from 'lucide-react'

/**
 * Standalone page for company employees to submit vendor assessments
 *
 * This page provides a direct link for internal users to submit TPRM assessments
 * without navigating through the full admin interface.
 */
const SubmitAssessmentPage: React.FC = () => {
  const navigate = useNavigate()
  const authCheckInProgress = useRef(false)

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      // Prevent concurrent auth checks
      if (authCheckInProgress.current) {
        return
      }

      authCheckInProgress.current = true

      try {
        const response = await fetch('/api/auth-validate', {
          credentials: 'include'
        })

        if (!response.ok) {
          // Not authenticated, redirect to login with return URL
          window.location.href = '/admin?redirect=/submit-assessment'
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        // Show error message before redirect for network failures
        alert('Authentication service is temporarily unavailable. Please try again in a moment.')
        // Delay redirect to allow user to see the message
        setTimeout(() => {
          window.location.href = '/admin'
        }, 1000)
      } finally {
        authCheckInProgress.current = false
      }
    }

    checkAuth()
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-primary-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Vendor Risk Assessment</h1>
                <p className="text-sm text-gray-600">Submit a new vendor for security review</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center py-12">
            <Shield className="h-16 w-16 text-primary-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Submit Vendor Assessment
            </h2>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Use the TPRM section in the admin dashboard to submit new vendor assessments.
              You'll be able to provide vendor details and our AI will analyze their security posture.
            </p>
            <button
              onClick={() => {
                window.location.href = '/admin#tprm-assessments'
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              <Shield className="h-5 w-5" />
              Go to Submit Assessment
            </button>
          </div>

          {/* Instructions */}
          <div className="mt-8 border-t border-gray-200 pt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">How to Submit an Assessment</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                  1
                </span>
                <span>Click "Go to TPRM Dashboard" above or navigate to the TPRM section in the admin panel</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                  2
                </span>
                <span>Click on the "Assessments" tab</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                  3
                </span>
                <span>Click the "Add Vendor" button to start a new assessment</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                  4
                </span>
                <span>Fill in the vendor information - our AI will help analyze their security posture</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                  5
                </span>
                <span>Submit the assessment for review by your security team</span>
              </li>
            </ol>
          </div>

          {/* Quick Link */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  Bookmark This Link
                </h4>
                <p className="text-sm text-blue-800 mb-2">
                  Share this URL with employees who need to submit vendor assessments:
                </p>
                <div className="bg-white rounded px-3 py-2 text-sm text-blue-900 font-mono border border-blue-300">
                  {window.location.origin}/submit-assessment
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubmitAssessmentPage
